import React, { useEffect, useState } from "react";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import './Panel.scss';
import { getAPI, postAPI } from "../../Services/APIServices";
import { McLoadingIndicator, McTab, McTabBar, McTag } from "@maersk-global/mds-react-wrapper";
import { formatDate } from '../Table/Table';
interface ContainerDetailsPanelProps {
    isOpen: boolean;
    containerId: number | null;
    containerNumber: string | null;
    bookingNumber: string | null;
    isReefer: string | null;
    containerNumbers: string[];
    onClose: () => void;
}
interface BookingDetails {
    bookingNumber: string;
    transportStartDate: string | Date;
    shipmentStatus: string;
    brand: string;
    tradeLaneName: string;
    originLocationName: string;
    destinationLocationName: string;
    originCluster: string;
    destinationCluster: string;
    bookedByName: string;
    consigneeName: string;
    shipperName: string;
}
interface JourneyLogDetail {
    containerNumber: string;
    bookingNumber: string;
    move: string;
    activity: string;
    nextActivity: string;
    lastUpdateTimestamp: Date | null;
    moveSequence: number;
    country: string;
    city: string;
    location: string;
    vesselName: string;
    isEmpty: string;
}
interface ReeferContainerDetail {
    containerNumber: string;
    deviceId: string;
    gpsLatitude: number | null;
    gpsLongitude: number | null;
    gpsLockState: string;
    gpsSatelliteCount: number | null;
    batteryVoltage: number | null;
    co2Reading: number | null;
    co2Setpoint: number | null;
    controllerType: string;
    geofenceSiteCode: string;
    powerSource: string;
}
const ContainerDetailsPanel: React.FC<ContainerDetailsPanelProps> = ({ isOpen, containerId, containerNumber, bookingNumber, isReefer, containerNumbers, onClose }) => {
    const [selectedTab, setSelectedTab] = useState<string>("Booking Details");
    const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
    const [journeyLogDetails, setJourneyLogDetails] = useState<JourneyLogDetail[]>([]);
    const [reeferContainerDetails, setReeferContainerDetails] = useState<ReeferContainerDetail[]>([]);
    const [bookingDetailsLoaded, setBookingDetailsLoaded] = useState(false);
    const [filteredJourneyLogDetails, setFilteredJourneyLogDetails] = useState<JourneyLogDetail[]>([]);
    const [filteredReeferContainerDetails, setFilteredReeferContainerDetails] = useState<ReeferContainerDetail[]>([]);
    const [journeyLogDetailsLoaded, setJourneyLogDetailsLoaded] = useState(false);
    const [reeferContainerDetailsLoaded, setReeferContainerDetailsLoaded] = useState(false);
    const [selectedContainer, setSelectedContainer] = useState<string | null>(containerNumbers[0] || null);
    const [journeyLogLoading, setJourneyLogLoading] = useState(false);
    const [reeferContainerLoading, setReeferContainerLoading] = useState(false);
    const [bookingDetailsError, setBookingDetailsError] = useState<string | null>(null);
    const [journeyLogError, setJourneyLogError] = useState<string | null>(null);
    const [reeferContainerError, setReeferContainerError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [bookingAbortController, setBookingAbortController] = useState<AbortController | null>(null);
    const [journeyLogAbortController, setJourneyLogAbortController] = useState<AbortController | null>(null);

    const formatCustomDate = (date: Date | null):string => {
        if(!date) return "";
        const day = date.getUTCDate().toString().padStart(2, "0");
        const month = date.toLocaleString('en-GB', { month: "short",timeZone:"UTC" });
        const year = date.getFullYear();
        const hours = date.getUTCHours().toString().padStart(2, "0");
        const minutes = date.getUTCMinutes().toString().padStart(2, "0");
        const seconds = date.getUTCSeconds().toString().padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    }

    useEffect(() => {
        if (isOpen) {
            setSelectedTab("Booking Details");
              const uniqueContainers=[...new Set(containerNumbers)];
            setSelectedContainer(uniqueContainers[0] || null);
        
        }
    }, [isOpen, containerNumbers]);
    
    

    const fetchBookingDetails = async () => {
        console.log("Booking details called");
        const controller = new AbortController(); 
        setBookingAbortController(controller); 
        setLoading(true);
        setBookingDetailsError(null);
        try {
            const data = await getAPI(`api/Container/GetBookingDetailsAsync/${bookingNumber}`,
                undefined,
                {
                signal: controller.signal,
            });
            if (data) {
                data.tradeLaneName = data.tradeLaneName ?? "N/A";
                data.shipmentStatus = data.shipmentStatus ?? "N/A";
                data.brand=data.brand ?? "N/A";
                data.originLocationName=data.originLocationName ?? "N/A";
                data.destinationLocationName=data.destinationLocationName ?? "N/A";
                data.originCluster=data.originCluster ?? "N/A";
                data.destinationCluster=data.destinationCluster ?? "N/A"; 
                data.bookedByName=data.bookedByName ?? "N/A";
                data.consigneeName=data.consigneeName ?? "N/A";
                data.shipperName=data.shipperName ?? "N/A";
                data.transportStartDate = data.transportStartDate ? new Date(data.transportStartDate) : null;
                setBookingDetails(data);
                setBookingDetailsLoaded(true);
            } else {
                setBookingDetails(null);
                console.error("Failed to fetch booking details");
            }
        } catch (error: any) {
            if (error.name === "AbortError") {
                console.log("Previous request aborted.");
                return;
            }
            if (error.data?.status === 204) {
                setBookingDetails(null);
                console.warn("No content (204) returned for booking details.")
            } else if (error.response?.status === 500 || error.response?.status === 504) {
                setBookingDetailsError("Error retrieving details. Please try again");
            } else {
                console.error("Error fetching booking details", error);
            }

        } finally {
            setLoading(false);
        }
    };

    const fetchJourneyLogDetails = async () => {
        if (journeyLogDetails.some(detail => detail.containerNumber === selectedContainer)) {
            console.log("Using cached journey log details");
            setFilteredJourneyLogDetails(
                journeyLogDetails.filter(item => item.containerNumber === selectedContainer)
            );
            return;
        }
    
        const controller = new AbortController();
        setJourneyLogAbortController(controller);
        setJourneyLogLoading(true);
        setJourneyLogError(null);
        console.log("fetchJourneyLogDetails called");

        try {
            const uniqueContainers = [...new Set(containerNumbers)];
            const payload = {
                containerNumber: uniqueContainers,
                bookingNumber,
            };
            console.log("Payload:", payload);
            const data = await postAPI(`api/Container/GetContainerJourneyDetailsAsync/`, payload,"json",{signal: controller.signal});
            console.log("API Response for Journey Log:", data);
            if (data) {
                const parsedData = data.map((item: any) => ({
                    containerNumber: item.containerNumber,
                    bookingNumber: item.bookingNumber,
                    move: item.move ?? "N/A",
                    activity: item.activity ?? "N/A",
                    nextActivity: item.nextActivity ?? "N/A",
                    lastUpdateTimestamp: item.lastUpdateTimestamp ? new Date(item.lastUpdateTimestamp) : null,
                    moveSequence: item.moveSequence,
                    country: item.country ?? "N/A",
                    city: item.city ?? "N/A",
                    location: item.location ?? "N/A",
                    vesselName: item.vesselName ?? "N/A",
                    isEmpty: item.isEmpty ?? "N/A",
                }));
                setJourneyLogDetails(parsedData);
                setJourneyLogDetailsLoaded(true);

                if (selectedContainer) {
                    setFilteredJourneyLogDetails(parsedData.filter(item => item.containerNumber === selectedContainer));
                }
            }
        } catch (error: any) {
            if (error.name === "AbortError") {
                console.log("Previous journey log request aborted.");
                return;
            }
            if (error.response?.status === 500 || error.response?.status === 504) {
                setJourneyLogError("Error retrieving details. Please try again");
            } else {
                console.error("Error fetching journey log details", error);
                setJourneyLogError("Unexpected error occured. Please try again");
            }
        } finally {
            setJourneyLogLoading(false);
            // setJourneyLogDetailsLoaded(true);
        }
    };

    const fetchReeferContainerDetails = async () => {
        if (reeferContainerDetails.some(detail => detail.containerNumber === selectedContainer)) {
            console.log("Using chached reefer container details");
            setFilteredReeferContainerDetails(
                reeferContainerDetails.filter(item => item.containerNumber === selectedContainer)
            );
            return;
        }
        setReeferContainerLoading(true);
        setReeferContainerError(null);
        try {
            const uniqueContainers = [...new Set(containerNumbers)];
            const payload = uniqueContainers;
            const data = await postAPI(`api/Container/GetContainerTelematryDetailAsync/`, payload);
            if (data) {
                const parsedData = data.map((item: any) => ({
                    containerNumber: item.containerNumber,
                    deviceId: item.deviceId,
                    gpsLatitude: item.gpsLatitude,
                    gpsLongitude: item.gpsLongitude,
                    gpsLockState: item.gpsLockState,
                    gpsSatelliteCount: item.gpsSatelliteCount,
                    batteryVoltage: item.batteryVoltage,
                    co2Reading: item.co2Reading,
                    co2Setpoint: item.co2Setpoint,
                    controllerType: item.controllerType,
                    geofenceSiteCode: item.geofenceSiteCode,
                    powerSource: item.powerSource,

                })
                );
                setReeferContainerDetails(parsedData);
                setReeferContainerDetailsLoaded(true);
                if (selectedContainer) {
                    setFilteredReeferContainerDetails(parsedData.filter(item => item.containerNumber === selectedContainer));
                }
            }
        } catch (error: any) {
            if (error.response?.status === 500 || error.response?.status === 504) {
                setReeferContainerError("Error retrieving details. Please try again");
            } else {
                console.error("Error fetching reefer container details", error);
                setReeferContainerError("Unexpected Error Occured. Please try again.")
            }

        } finally {
            setReeferContainerLoading(false);
        }
    };


    useEffect(() => {
        console.log("Selected tab:", selectedTab);
        console.log("Container number:", containerNumber);
        console.log("Booking number:", bookingNumber);
        setJourneyLogDetailsLoaded(false);
        setReeferContainerDetailsLoaded(false);
        // setJourneyLogDetails([]); 
        // setReeferContainerDetails([]);
        // setBookingDetails(null);

        if (isOpen && selectedContainer) {
            switch (selectedTab) {
                case "Booking Details":
                    if (bookingNumber && !bookingDetailsLoaded) {
                        fetchBookingDetails();
                    }
                    break;
                case "Journey Log":
                    if (selectedContainer && bookingNumber && !journeyLogDetailsLoaded) {
                        const cachedData = journeyLogDetails.filter(item => item.containerNumber === selectedContainer);
                        if (cachedData.length > 0) {
                            setFilteredJourneyLogDetails(cachedData);
                            setJourneyLogLoading(false);
                        } else if (!journeyLogDetailsLoaded) {
                            fetchJourneyLogDetails();
                        }
                    }
                    break;
                case "Reefer Container Telemetry":
                    if (selectedContainer && !reeferContainerDetailsLoaded) {
                        const cachedReeferData = reeferContainerDetails.filter(item => item.containerNumber === selectedContainer);
                        if (cachedReeferData.length > 0) {
                            setFilteredReeferContainerDetails(cachedReeferData);
                            setReeferContainerLoading(false);
                        } else if (!reeferContainerDetailsLoaded) {
                            fetchReeferContainerDetails();
                        }
                    }
                    break;
                default:
                    console.warn("Unhandles Tab", selectedTab);
            }



        }
    }, [selectedContainer, selectedTab, isOpen, containerNumber, bookingNumber, journeyLogDetails, reeferContainerDetails]);

    const handleContainerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const container = event.target.value;
        setSelectedContainer(container);
        setBookingDetailsLoaded(false);
        const cachedData = journeyLogDetails.filter(item => item.containerNumber === container);
        if (cachedData.length > 0) {
            setFilteredJourneyLogDetails(cachedData);
            setJourneyLogLoading(false);
        }
        else {
            // setJourneyLogDetailsLoaded(false);
            setFilteredJourneyLogDetails([]);
        }
        // setJourneyLogDetailsLoaded(false);
        // setJourneyLogDetails([]);
        setJourneyLogLoading(true);
        const cachedReeferData = reeferContainerDetails.filter(item => item.containerNumber === container);
        if (cachedReeferData.length > 0) {
            setFilteredReeferContainerDetails(cachedReeferData);
            setReeferContainerLoading(false);
        }
        else {
            // setReeferContainerDetailsLoaded(false);
            setFilteredReeferContainerDetails([]);
        }
        // setReeferContainerDetails([]);
        // setReeferContainerDetailsLoaded(false);
        setLoading(true);
    };
    useEffect(() => {
        if (!isOpen) {
            setSelectedTab("Booking Details");
            setBookingDetails(null);
            if (bookingAbortController) {
                bookingAbortController.abort();
                setBookingAbortController(null);
            }
            if(journeyLogAbortController){
                journeyLogAbortController.abort();
                setJourneyLogAbortController(null);
            }
            setJourneyLogDetails([]);
            setReeferContainerDetails([]);
            setBookingDetailsLoaded(false);
            setJourneyLogDetailsLoaded(false);
            setReeferContainerDetailsLoaded(false);

        } else {
            setSelectedTab("Booking Details");
            const uniqueContainers = [...new Set(containerNumbers)];
            setSelectedContainer(uniqueContainers[0] || null);

        }
    }, [isOpen, containerNumbers]);


    const handleTabChange = (event: CustomEvent) => {
        console.log("Tab change event:", event.detail);
        const newTab = event.detail?.label?.trim();
        if (newTab) {
            setSelectedTab(newTab);
         } else {
             console.error("Tab change event does not have a valid label:", event.detail);
         }
    };

    const renderBookingDetails = () => {
        if(loading){
            return <div className="loading-center">
                <McLoadingIndicator appearance="primary"></McLoadingIndicator>
            </div>
         }
        if (bookingDetailsError) {
            return <div className="loading-center">{bookingDetailsError}</div>;
        }
        if (!bookingDetails) {
            return <div className="loading-center">No Booking Details found</div>
        }
        return (
            <div className="booking-details-grid">
                <div><strong>Booking Number</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.bookingNumber}>{bookingDetails.bookingNumber}</div>
                </div>
                <div><strong>Transport Start Date</strong><br />{formatDate(bookingDetails.transportStartDate as string)}</div>
                <div><strong>Shipment Status</strong><br /><div>
                    <McTag
                        appearance={
                            bookingDetails.shipmentStatus === "Active" ? "info"
                                : bookingDetails.shipmentStatus === "Migrated Off" ? "neutral-weak"
                                    : bookingDetails.shipmentStatus === "Cancelled by Carrier" ? "warning"
                                        : bookingDetails.shipmentStatus === "Completed" ? "success"
                                            : bookingDetails.shipmentStatus === "Abandoned" ? "error"
                                                : bookingDetails.shipmentStatus === "Cancelled by Customer" ? "warning"
                                                    : "neutral-weak"
                        }
                        fit="small"
                        dismiss={() => { }}
                    >
                        {bookingDetails.shipmentStatus}
                    </McTag>
                </div></div>
                <div><strong>Brand</strong><br />{bookingDetails.brand}</div>

                <div><strong>TradeLane Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.tradeLaneName}>{bookingDetails.tradeLaneName}</div>
                </div>
                <div><strong>Origin Location Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.originLocationName}>{bookingDetails.originLocationName}</div>
                </div>
                <div><strong>Destination Location Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.destinationLocationName}>{bookingDetails.destinationLocationName}</div>
                </div>
                <div><strong>Origin Cluster</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.originCluster}>{bookingDetails.originCluster}</div>
                </div>

                <div><strong>Destination Cluster</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.destinationCluster}>{bookingDetails.destinationCluster}</div>
                </div>
                <div><strong>Booked By Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.bookedByName}>{bookingDetails.bookedByName}</div>
                </div>
                <div><strong>Consignee Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.consigneeName}>{bookingDetails.consigneeName}</div>
                </div>
                <div><strong>Shipper Name</strong><br />
                    <div className="ellipsis-text" title={bookingDetails.shipperName}>{bookingDetails.shipperName}</div>
                </div>
            </div>
        );
    };
    useEffect(() => {
        if (!bookingNumber) {
            setBookingDetails(null);
        }
    }, [bookingNumber]);

    const uniqueContainers = [...new Set(containerNumbers)];

    const renderJourneyLogDetails = () => {
        if(journeyLogLoading){
            return <div className="loading-center">
                <McLoadingIndicator appearance="primary"></McLoadingIndicator>
            </div>
           }
        if (journeyLogError) {
            return <div className="loading-center">{journeyLogError}</div>;
        }
        if(filteredJourneyLogDetails.length === 0){
            return <div className="loading-center">No Journey Log Details Found.</div>
           }
    

        return (
            < >
                <div className="journey-log-header">
                    {uniqueContainers.length > 1 ? (
                        <>
                            <strong>Container Number: </strong>
                            <select value={selectedContainer || ""} onChange={handleContainerChange}>
                                {containerNumbers.map((number) => (
                                    <option key={number} value={number}>{number}</option>
                                ))}
                            </select>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </>
                    ) : (
                        <>
                            <strong>Container Number: </strong>{selectedContainer}
                        </>
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <strong>Booking Number: </strong>{bookingNumber}
                </div>
                    <table className="journey-log-table table-border">
                        <thead>
                            <tr>
                                {/* <th>Container Number</th> */}
                                {/* <th>Booking Number</th> */}
                                <th>Move</th>
                                <th>Activity</th>
                                <th>Move Timestamp</th>
                                {/* <th>Move Sequence</th> */}
                                <th>Country</th>
                                <th>City</th>
                                <th>Location</th>
                                <th>Vessel Name</th>
                                <th>Is Empty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredJourneyLogDetails.slice()
                                .sort((a, b) => {
                                    if (a.lastUpdateTimestamp && b.lastUpdateTimestamp) {
                                        return new Date(b.lastUpdateTimestamp).getTime() - new Date(a.lastUpdateTimestamp).getTime();

                                    }
                                    return 0;
                                })
                                .map((log, index) => (
                                    <tr key={index}>
                                        {/* <td>{log.containerNumber}</td> */}
                                        {/* <td>{log.bookingNumber}</td> */}
                                        <td>{log.move}</td>
                                        <td>{log.activity}</td>
                                        <td>{formatCustomDate(log.lastUpdateTimestamp)}</td>
                                        {/* <td>{log.moveSequence}</td> */}
                                        <td>{log.country}</td>
                                        <td>{log.city}</td>
                                        <td>{log.location}</td>
                                        <td>{log.vesselName}</td>
                                        <td>{log.isEmpty}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                
            </>

        );
    };
    const renderReeferContainerDetails = () => {
        if(reeferContainerLoading){
            return <div className="loading-center">
                <McLoadingIndicator appearance="primary"></McLoadingIndicator>
            </div>
           }
        if (reeferContainerError) {
            return <div className="loading-center">{reeferContainerError}</div>;
        }
        if(filteredReeferContainerDetails.length === 0){
            return <div className="loading-center">No Reefer Container Details Found.</div>
        }


        return (
            <>
                <div className="journey-log-header">
                    {uniqueContainers.length > 1 ? (
                        <>
                            <strong>Container Number: </strong>
                            <select value={selectedContainer || ""} onChange={handleContainerChange}>
                                {containerNumbers.map((number) => (
                                    <option key={number} value={number}>
                                        {number}
                                    </option>
                                ))}
                            </select>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </>
                    ) : (
                        <>
                            <strong>Container Number: </strong>{selectedContainer}
                        </>
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <strong>Booking Number: </strong>{bookingNumber}
                </div>
            
                    <table className="journey-log-table table-border">
                        <thead>
                            <tr>
                                <th>Container Number</th>
                                <th>Device ID</th>
                                <th>GPS Latitude</th>
                                <th>GPS Longitude</th>
                                <th>GPS Lock State</th>
                                <th>GPS Satellite Count</th>
                                <th>Battery Voltage</th>
                                <th>CO2 Reading</th>
                                <th>CO2 Setpoint</th>
                                <th>Controller Type</th>
                                <th>Geofence Site Code</th>
                                <th>Power Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReeferContainerDetails.map((detail, index) => (
                                <tr key={index}>
                                    <td>{detail.containerNumber}</td>
                                    <td>{detail.deviceId}</td>
                                    <td>{detail.gpsLatitude ?? "N/A"}</td>
                                    <td>{detail.gpsLongitude ?? "N/A"}</td>
                                    <td>{detail.gpsLockState}</td>
                                    <td>{detail.gpsSatelliteCount ?? "N/A"}</td>
                                    <td>{detail.batteryVoltage ?? "N/A"}</td>
                                    <td>{detail.co2Reading ?? "N/A"}</td>
                                    <td>{detail.co2Setpoint ?? "N/A"}</td>
                                    <td>{detail.controllerType}</td>
                                    <td>{detail.geofenceSiteCode ?? "N/A"}</td>
                                    <td>{detail.powerSource}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                
            </>
        );
    };

    // const renderTabContent = () => {
    //     switch (selectedTab) {
    //         case "Booking Details":
    //             return renderBookingDetails();
    //         case "Journey Log":
    //             // if (journeyLogLoading) return <div className="loading-center">Loading Journey Log...</div>;
    //             // if (journeyLogError) return <div className="loading-center">{journeyLogError}</div>;
    //             // if (filteredJourneyLogDetails.length === 0) return <div className="loading-center">No Journey Log details available.</div>;
    //             return renderJourneyLogDetails();
    //         case "Reefer Container Telemetry":
    //             // if (reeferContainerLoading) return <div className="loading-center">Loading Reefer Container Telemetry...</div>;
    //             // if (reeferContainerError) return <div className="loading-center">{reeferContainerError}</div>;
    //             // if (filteredReeferContainerDetails.length === 0) return <div className="loading-center">No Reefer Telemetry details available.</div>;
    //             return renderReeferContainerDetails();
    //         default:
    //             return <div className="loading-center">Content not available</div>
    //     }
    // };

    return (
        <McModal
            // heading="Container Details"
            zindex={99999}
            open={isOpen}
            closed={onClose}
            closing={onClose}
            className="custom-modal-centered"
            width="1040px"
            height="450px"
        >
            <div className={`container-details-panel ${isOpen ? "open" : ""}`}>
                <McTabBar tabchange={handleTabChange} currentindex={["Booking Details","Journey Log","Reefer Container Telemetry"].indexOf(selectedTab)}>
                <McTab slot="tab" label="Booking Details" onClick={() => handleTabChange(new CustomEvent("tabChange", { detail: { label: "Booking Details" } }))}></McTab>
                <div slot="panel">{selectedTab === "Booking Details" && renderBookingDetails()}</div>
                <McTab slot="tab" label="Journey Log" onClick={() => handleTabChange(new CustomEvent("tabChange", { detail: { label: "Journey Log" } }))}></McTab>
                <div slot="panel">{selectedTab === "Journey Log" && renderJourneyLogDetails()}</div>
                {/* <McTab slot="tab" label="Reefer Container Telemetry" onClick={() => handleTabChange(new CustomEvent("tabChange", { detail: { label: "Reefer Container Telemetry" } }))}></McTab>
                <div slot="panel">{selectedTab === "Reefer Container Telemetry" && renderReeferContainerDetails()}</div> */}
                </McTabBar>

            </div>

            <McButton
                slot="secondaryAction"
                appearance="neutral"
                variant="filled"
                dialogaction="cancel"
                fit="small"
                onClick={onClose}
            >
                Close
            </McButton>
        </McModal>
    );
};

export default ContainerDetailsPanel;